// src/pages/OrderPage.js (with dynamic pricing from admin - NO MINIMUM ORDER)
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api/api"; // Import your api instance
import { 
  FaFileAlt, 
  FaGraduationCap, 
  FaLanguage, 
  FaFileWord, 
  FaPaperPlane,
  FaInfoCircle,
  FaClock,
  FaBook,
  FaUniversity,
  FaQuoteRight,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaCalendarDay,
  FaHourglassStart,
  FaSpinner
} from "react-icons/fa";
import "./OrderPage.css";

const OrderPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useContext(AuthContext);

  // State for dynamic pricing data
  const [pricingConfig, setPricingConfig] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  // Define all constant arrays at the top
  const academicLevels = [
    "High School", "College", "Undergraduate", "Bachelor", 
    "Master", "PhD", "Doctoral"
  ];

  const subjects = [
    "English Literature", "History", "Psychology", "Sociology",
    "Philosophy", "Political Science", "Economics", "Business",
    "Marketing", "Finance", "Computer Science", "Engineering",
    "Mathematics", "Physics", "Chemistry", "Biology",
    "Medicine", "Nursing", "Law", "Education",
    "Art History", "Music Theory", "Other"
  ];

  const languageOptions = [
    "English (US)", "English (UK)", "Spanish", "French", 
    "German", "Italian", "Portuguese", "Chinese",
    "Japanese", "Arabic", "Russian", "Other"
  ];

  const spacingOptions = ["Single", "Double", "1.5"];

  const formatOptions = ["APA", "MLA", "Chicago", "Harvard", "IEEE", "Other"];

  // Get current date/time for min values
  const getCurrentDateTime = () => {
    const now = new Date();
    // Add 6 hours for minimum deadline
    now.setHours(now.getHours() + 6);
    return now;
  };

  const getMaxDateTime = () => {
    const max = new Date();
    max.setDate(max.getDate() + 30);
    return max;
  };

  const formatDateTimeForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeFromInput = (value) => {
    if (!value) return null;
    return new Date(value);
  };

  const [formData, setFormData] = useState({
    // Order Details (matches Django model)
    title: "",
    subject: "",
    academic_level: "High School",
    pages: 1,
    deadline: null,
    deadlineInput: "", // For the datetime-local input
    instructions: "",
    budget: 0,
    
    // Additional fields
    language: "English (US)",
    format: "APA",
    spacing: "Double",
    slides: 0,
    sources: 0,
    
    // Additional services
    plagiarismReport: false,
    executiveSummary: false,
    expertWriter: false,
    
    // Files
    files: [],
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState({
    basePrice: 0,
    rushFee: 0,
    serviceFee: 0,
    total: 0
  });

  // Fetch pricing configuration from backend
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoadingPricing(true);
        
        // CRITICAL: Use the full URL with /api prefix
        const url = '/api/administration/pricing/';
        console.log("🔍 Fetching pricing from:", url);
        
        // Add cache-busting parameter in production
        const finalUrl = process.env.NODE_ENV === 'production' 
          ? `${url}?_=${Date.now()}`
          : url;
        
        const response = await api.get(finalUrl);
        
        // Log the response for debugging
        console.log("📦 Response status:", response.status);
        console.log("📦 Response headers:", response.headers);
        console.log("✅ Pricing config loaded:", response.data);
        
        // Validate response data has the expected structure
        if (!response.data || typeof response.data !== 'object') {
          console.error("❌ Invalid response format:", response.data);
          throw new Error("Invalid response format");
        }
        
        // Check if we got HTML instead of JSON
        if (typeof response.data === 'string' && 
            (response.data.trim().startsWith('<!DOCTYPE') || response.data.trim().startsWith('<html'))) {
          console.error("❌ Received HTML instead of JSON!");
          throw new Error("Received HTML response from server");
        }
        
        // Make sure academic_levels exists
        if (!response.data.academic_levels) {
          console.warn("⚠️ API response missing academic_levels, using defaults");
          response.data.academic_levels = {
            "High School": 10,
            "College": 12,
            "Undergraduate": 15,
            "Bachelor": 18,
            "Master": 22,
            "PhD": 28,
            "Doctoral": 35
          };
        }
        
        // Ensure deadlines array exists
        if (!response.data.deadlines) {
          response.data.deadlines = [
            { range: '0-24', percentage: 80, display_name: 'Urgent (24 hours or less)' },
            { range: '25-72', percentage: 60, display_name: 'Fast (1-3 days)' },
            { range: '73-168', percentage: 30, display_name: 'Standard (3-7 days)' },
            { range: '169-336', percentage: 10, display_name: 'Relaxed (7-14 days)' },
            { range: '337+', percentage: 0, display_name: 'Extended (14+ days)' }
          ];
        }
        
        // Ensure additional_services exists
        if (!response.data.additional_services) {
          response.data.additional_services = [
            { type: 'PLAGIARISM', fee_type: 'FIXED', price: 10, name: 'Plagiarism Report', description: 'Comprehensive Turnitin plagiarism report' },
            { type: 'EXECUTIVE_SUMMARY', fee_type: 'FIXED', price: 15, name: 'Executive Summary', description: '1-page summary of key points' },
            { type: 'EXPERT_WRITER', fee_type: 'FIXED', price: 25, name: 'Expert Writer', description: 'Top-rated specialist in your field' }
          ];
        }
        
        // Ensure site_settings exists
        if (!response.data.site_settings) {
          response.data.site_settings = {
            minimum_order: 0,
            enable_rush_fees: true
          };
        }
        
        setPricingConfig(response.data);
        setPricingError(null);
      } catch (err) {
        console.error("❌ Failed to fetch pricing:", err);
        
        let errorMessage = "Failed to load pricing data. Using default prices.";
        if (err.response?.status === 404) {
          errorMessage = "Pricing API endpoint not found. Using default prices.";
        } else if (err.response?.status === 500) {
          errorMessage = "Server error loading pricing. Using default prices.";
        } else if (err.message && err.message.includes("HTML")) {
          errorMessage = "Received HTML instead of JSON. API endpoint may be misconfigured.";
        }
        
        setPricingError(errorMessage);
        
        // Set default pricing config as fallback
        setPricingConfig({
          academic_levels: {
            "High School": 10,
            "College": 12,
            "Undergraduate": 15,
            "Bachelor": 18,
            "Master": 22,
            "PhD": 28,
            "Doctoral": 35
          },
          deadlines: [
            { range: '0-24', percentage: 80, display_name: 'Urgent (24 hours or less)' },
            { range: '25-72', percentage: 60, display_name: 'Fast (1-3 days)' },
            { range: '73-168', percentage: 30, display_name: 'Standard (3-7 days)' },
            { range: '169-336', percentage: 10, display_name: 'Relaxed (7-14 days)' },
            { range: '337+', percentage: 0, display_name: 'Extended (14+ days)' }
          ],
          additional_services: [
            { type: 'PLAGIARISM', fee_type: 'FIXED', price: 10, name: 'Plagiarism Report', description: 'Turnitin report included' },
            { type: 'EXECUTIVE_SUMMARY', fee_type: 'FIXED', price: 15, name: 'Executive Summary', description: '1-page summary of key points' },
            { type: 'EXPERT_WRITER', fee_type: 'FIXED', price: 25, name: 'Expert Writer', description: 'Top-rated specialist in your field' }
          ],
          site_settings: {
            minimum_order: 0,
            enable_rush_fees: true
          }
        });
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricing();
  }, []);

  // Calculate price whenever relevant fields change
  useEffect(() => {
    // Only calculate if pricing config is fully loaded with academic_levels
    if (pricingConfig && pricingConfig.academic_levels) {
      console.log("💰 Calculating price with pricing config:", pricingConfig);
      calculatePrice();
    } else {
      console.log("⏳ Waiting for pricing config to load before calculating price...");
    }
  }, [
    formData.academic_level, 
    formData.pages, 
    formData.deadline,
    formData.plagiarismReport,
    formData.executiveSummary,
    formData.expertWriter,
    formData.slides,
    formData.subject,
    pricingConfig
  ]);

  const calculatePrice = () => {
    // CRITICAL SAFETY CHECK: Ensure pricingConfig and academic_levels exist
    if (!pricingConfig) {
      console.log("⚠️ calculatePrice skipped: pricingConfig is null");
      return;
    }
    
    if (!pricingConfig.academic_levels) {
      console.log("⚠️ calculatePrice skipped: pricingConfig.academic_levels is undefined");
      return;
    }

    console.log("💰 Calculating price for academic level:", formData.academic_level);
    console.log("💰 Available academic levels:", pricingConfig.academic_levels);

    // Get base price per page from config with safe fallback
    const basePricePerPage = pricingConfig.academic_levels[formData.academic_level] || 15;
    let basePrice = basePricePerPage * formData.pages;
    
    // Apply page count discounts if available
    if (pricingConfig.page_discounts && pricingConfig.page_discounts.length > 0) {
      const applicableDiscount = pricingConfig.page_discounts.find(
        d => formData.pages >= d.min_pages && 
             (!d.max_pages || formData.pages <= d.max_pages)
      );
      
      if (applicableDiscount) {
        if (applicableDiscount.modifier_type === 'discount') {
          basePrice = basePrice * (1 - applicableDiscount.modifier_value / 100);
        } else if (applicableDiscount.modifier_type === 'premium') {
          basePrice = basePrice * (1 + applicableDiscount.modifier_value / 100);
        }
      }
    }
    
    // Apply subject multipliers if available
    if (pricingConfig.subject_multipliers && formData.subject) {
      const multiplier = pricingConfig.subject_multipliers[formData.subject];
      if (multiplier) {
        basePrice = basePrice * multiplier;
      }
    }
    
    // Rush fee calculation based on deadline
    let rushFee = 0;
    if (formData.deadline && pricingConfig.site_settings?.enable_rush_fees) {
      const now = new Date();
      const deadline = new Date(formData.deadline);
      const hoursDifference = Math.max(1, Math.ceil((deadline - now) / (1000 * 60 * 60)));
      
      // Find applicable rush fee tier
      let rushPercentage = 0;
      if (hoursDifference <= 24) {
        const rushTier = pricingConfig.deadlines?.find(d => d.range === '0-24');
        rushPercentage = rushTier ? rushTier.percentage : 80;
      } else if (hoursDifference <= 72) {
        const rushTier = pricingConfig.deadlines?.find(d => d.range === '25-72');
        rushPercentage = rushTier ? rushTier.percentage : 60;
      } else if (hoursDifference <= 168) {
        const rushTier = pricingConfig.deadlines?.find(d => d.range === '73-168');
        rushPercentage = rushTier ? rushTier.percentage : 30;
      }
      
      rushFee = basePrice * (rushPercentage / 100);
    }

    // Additional services
    let serviceFee = 0;
    
    // Plagiarism Report
    if (formData.plagiarismReport && pricingConfig.additional_services) {
      const service = pricingConfig.additional_services.find(s => s.type === 'PLAGIARISM');
      if (service) {
        if (service.fee_type === 'FIXED') {
          serviceFee += service.price;
        } else if (service.fee_type === 'PER_PAGE') {
          serviceFee += service.price_per_page * formData.pages;
        } else if (service.fee_type === 'PERCENTAGE') {
          serviceFee += basePrice * (service.percentage / 100);
        }
      } else {
        serviceFee += 10; // Fallback
      }
    }
    
    // Executive Summary
    if (formData.executiveSummary && pricingConfig.additional_services) {
      const service = pricingConfig.additional_services.find(s => s.type === 'EXECUTIVE_SUMMARY');
      if (service) {
        if (service.fee_type === 'FIXED') {
          serviceFee += service.price;
        } else if (service.fee_type === 'PER_PAGE') {
          serviceFee += service.price_per_page * formData.pages;
        } else if (service.fee_type === 'PERCENTAGE') {
          serviceFee += basePrice * (service.percentage / 100);
        }
      } else {
        serviceFee += 15; // Fallback
      }
    }
    
    // Expert Writer
    if (formData.expertWriter && pricingConfig.additional_services) {
      const service = pricingConfig.additional_services.find(s => s.type === 'EXPERT_WRITER');
      if (service) {
        if (service.fee_type === 'FIXED') {
          serviceFee += service.price;
        } else if (service.fee_type === 'PER_PAGE') {
          serviceFee += service.price_per_page * formData.pages;
        } else if (service.fee_type === 'PERCENTAGE') {
          serviceFee += basePrice * (service.percentage / 100);
        }
      } else {
        serviceFee += 25; // Fallback
      }
    }
    
    // Slides - make this configurable from backend too
    if (formData.slides > 0) {
      serviceFee += formData.slides * 5; // Fixed $5 per slide
    }

    // Calculate total
    let total = basePrice + rushFee + serviceFee;

    // Update form budget
    setFormData(prev => ({ ...prev, budget: Math.round(total) }));

    // Update price breakdown
    setPriceBreakdown({
      basePrice: Math.round(basePrice),
      rushFee: Math.round(rushFee),
      serviceFee: Math.round(serviceFee),
      total: Math.round(total)
    });

    console.log("💰 Price calculation complete:", {
      basePrice: Math.round(basePrice),
      rushFee: Math.round(rushFee),
      serviceFee: Math.round(serviceFee),
      total: Math.round(total)
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'deadlineInput') {
      const date = parseDateTimeFromInput(value);
      setFormData(prev => ({
        ...prev,
        deadlineInput: value,
        deadline: date
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Get rush fee tier description
  const getRushFeeDescription = () => {
    if (!formData.deadline || !pricingConfig?.deadlines) return "";
    
    const now = new Date();
    const deadline = new Date(formData.deadline);
    const hoursDifference = Math.ceil((deadline - now) / (1000 * 60 * 60));
    
    if (hoursDifference <= 24) {
      const tier = pricingConfig.deadlines.find(d => d.range === '0-24');
      return tier?.display_name || "Urgent (24 hours or less)";
    } else if (hoursDifference <= 72) {
      const tier = pricingConfig.deadlines.find(d => d.range === '25-72');
      return tier?.display_name || "Fast (1-3 days)";
    } else if (hoursDifference <= 168) {
      const tier = pricingConfig.deadlines.find(d => d.range === '73-168');
      return tier?.display_name || "Standard (3-7 days)";
    }
    return "Relaxed (More than 7 days)";
  };

  // SAFE getter functions with null checks
  const getServicePrice = (serviceType) => {
    if (!pricingConfig?.additional_services) return '';
    
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    if (!service) return '';
    
    if (service.fee_type === 'FIXED') {
      return `+$${service.price}`;
    } else if (service.fee_type === 'PER_PAGE') {
      return `+$${service.price_per_page}/page`;
    } else if (service.fee_type === 'PERCENTAGE') {
      return `+${service.percentage}%`;
    }
    return '';
  };

  const getServiceDescription = (serviceType) => {
    if (!pricingConfig?.additional_services) return '';
    
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    return service?.description || '';
  };

  const getServiceName = (serviceType) => {
    if (!pricingConfig?.additional_services) return '';
    
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    return service?.name || '';
  };

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    // Validation
    const validationErrors = {};

    if (!formData.title.trim()) {
      validationErrors.title = "Title is required";
    }

    if (!formData.subject.trim()) {
      validationErrors.subject = "Subject is required";
    }

    if (formData.pages < 1) {
      validationErrors.pages = "Must be at least 1 page";
    }

    if (!formData.deadline) {
      validationErrors.deadline = "Deadline is required";
    } else {
      const now = new Date();
      const selectedDeadline = new Date(formData.deadline);
      const minDeadline = getCurrentDateTime();
      if (selectedDeadline <= now) {
        validationErrors.deadline = "Deadline must be in the future";
      } else if (selectedDeadline < minDeadline) {
        validationErrors.deadline = "Minimum deadline is 6 hours from now";
      }
    }

    if (!formData.instructions.trim()) {
      validationErrors.instructions = "Instructions are required";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      // Format deadline to ISO string for Django
      const deadlineISO = formData.deadline.toISOString();
      
      // Prepare order data
      const orderData = {
        title: formData.title,
        subject: formData.subject,
        academic_level: formData.academic_level,
        pages: formData.pages,
        deadline: deadlineISO,
        instructions: formData.instructions,
        budget: priceBreakdown.total,
        
        // Additional data
        language: formData.language,
        format: formData.format,
        spacing: formData.spacing,
        slides: formData.slides,
        sources: formData.sources,
        plagiarismReport: formData.plagiarismReport,
        executiveSummary: formData.executiveSummary,
        expertWriter: formData.expertWriter,
        
        // Price breakdown
        priceBreakdown: priceBreakdown,
        
        // Files (will be handled separately)
        files: formData.files,
      };

      console.log("Order data prepared:", orderData);
      
      // Store in session storage for later use
      sessionStorage.setItem('orderData', JSON.stringify(orderData));
      
      // Check if user is logged in
      if (isAuthenticated) {
        navigate("/order-preview", { 
          state: { 
            orderData: orderData,
            userLoggedIn: true
          } 
        });
      } else {
        navigate("/login", { 
          state: { 
            message: "Please login to continue with your order",
            redirectTo: "/order-preview",
            redirectData: orderData
          } 
        });
      }

    } catch (err) {
      console.error("Order preparation error:", err);
      setErrors(prev => ({ 
        ...prev, 
        general: "Failed to process order. Please try again." 
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching pricing
  if (loadingPricing) {
    return (
      <div className="order-page-container">
        <div className="order-background-overlay"></div>
        <div className="order-loading-container">
          <FaSpinner className="order-loading-spinner" />
          <p>Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  // Show error state if pricing failed to load
  if (pricingError) {
    return (
      <div className="order-page-container">
        <div className="order-background-overlay"></div>
        <div className="order-error-container">
          <FaExclamationTriangle className="order-error-icon" />
          <h2>Unable to Load Pricing</h2>
          <p>{pricingError}</p>
          <button onClick={() => window.location.reload()} className="order-retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Ensure pricingConfig exists before rendering the form
  if (!pricingConfig) {
    return (
      <div className="order-page-container">
        <div className="order-background-overlay"></div>
        <div className="order-loading-container">
          <FaSpinner className="order-loading-spinner" />
          <p>Initializing order form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page-container">
      <div className="order-background-overlay"></div>
      
      <div className="order-form-container">
        {/* Header */}
        <div className="order-header">
          <div className="order-icon-container">
            <FaFileAlt className="order-main-icon" />
          </div>
          <h1 className="order-title">Place Your Order</h1>
          <p className="order-subtitle">Fill out the form below to get started with your writing project</p>
        </div>

        {/* Pricing Error Warning - This will only show if there's an error but we still have fallback pricing */}
        {pricingError && (
          <div className="order-warning-alert">
            <FaExclamationTriangle className="order-warning-icon" />
            <span>{pricingError}</span>
          </div>
        )}

        {/* Login Reminder */}
        {!isAuthenticated && (
          <div className="order-login-reminder">
            <FaExclamationTriangle className="order-reminder-icon" />
            <div className="order-reminder-content">
              <p>
                <strong>Not logged in?</strong> You'll be asked to login or create an account 
                after submitting this form to continue with your order.
              </p>
            </div>
          </div>
        )}

        {/* General Error */}
        {errors.general && (
          <div className="order-error-alert">
            <FaInfoCircle className="order-error-icon" />
            <span>{errors.general}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="order-form">
          <div className="order-form-grid">
            {/* Left Column */}
            <div className="order-form-column">
              {/* Basic Information Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaFileAlt className="order-section-icon" />
                  Basic Information
                </h3>
                
                <div className="order-form-group">
                  <label className="order-form-label">
                    Paper Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`order-input ${errors.title ? 'error' : ''}`}
                    placeholder="Enter your paper title"
                  />
                  {errors.title && (
                    <span className="order-error-text">{errors.title}</span>
                  )}
                </div>

                <div className="order-form-group">
                  <label className="order-form-label">
                    <FaGraduationCap className="order-input-icon" />
                    Academic Level
                  </label>
                  <select
                    name="academic_level"
                    value={formData.academic_level}
                    onChange={handleChange}
                    className="order-select"
                  >
                    {academicLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div className="order-form-group">
                  <label className="order-form-label">
                    Subject Area *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`order-select ${errors.subject ? 'error' : ''}`}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                  {errors.subject && (
                    <span className="order-error-text">{errors.subject}</span>
                  )}
                </div>

                <div className="order-form-group">
                  <label className="order-form-label">
                    Number of Pages *
                  </label>
                  <input
                    type="number"
                    name="pages"
                    value={formData.pages}
                    onChange={handleChange}
                    min="1"
                    max="100"
                    className={`order-input ${errors.pages ? 'error' : ''}`}
                  />
                  {errors.pages && (
                    <span className="order-error-text">{errors.pages}</span>
                  )}
                  <small className="order-hint">Approximately 275 words per page</small>
                </div>
              </div>

              {/* Deadline Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaClock className="order-section-icon" />
                  Deadline *
                </h3>
                
                <div className="order-form-group">
                  <label className="order-form-label">
                    <FaCalendarDay className="order-input-icon" />
                    Select Deadline Date & Time
                  </label>
                  
                  <div className="datetime-input-container">
                    <input
                      type="datetime-local"
                      name="deadlineInput"
                      value={formData.deadlineInput}
                      onChange={handleChange}
                      className={`order-input datetime-input ${errors.deadline ? 'error' : ''}`}
                      min={formatDateTimeForInput(getCurrentDateTime())}
                      max={formatDateTimeForInput(getMaxDateTime())}
                      step="900" // 15 minutes
                      required
                    />
                    <FaCalendarAlt className="datetime-input-icon" />
                  </div>
                  
                  {errors.deadline && (
                    <span className="order-error-text">{errors.deadline}</span>
                  )}
                  
                  <div className="order-deadline-info">
                    <FaInfoCircle className="order-info-icon" />
                    <div className="order-info-content">
                      <p><strong>Deadline Information:</strong></p>
                      <ul>
                        <li>Minimum deadline: 6 hours from now</li>
                        <li>Maximum deadline: 30 days from now</li>
                        <li>Select both date and time for precision</li>
                        <li>Urgent orders (24 hours or less) include rush fees</li>
                      </ul>
                    </div>
                  </div>
                  
                  {formData.deadline && (
                    <div className="order-deadline-preview">
                      <FaHourglassStart className="order-preview-icon" />
                      <div className="order-preview-content">
                        <p><strong>Selected Deadline:</strong></p>
                        <p className="order-deadline-display">
                          {formatDateForDisplay(formData.deadline)}
                        </p>
                        <p className="order-deadline-timing">
                          {getRushFeeDescription()}
                          {priceBreakdown.rushFee > 0 && (
                            <span className="order-rush-badge"> (Rush Fee Applied)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Instructions Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  Detailed Instructions *
                </h3>
                
                <div className="order-form-group">
                  <textarea
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleChange}
                    className={`order-textarea ${errors.instructions ? 'error' : ''}`}
                    placeholder="Provide detailed instructions, requirements, guidelines, sources to use, formatting requirements, and any specific requests..."
                    rows={8}
                  />
                  {errors.instructions && (
                    <span className="order-error-text">{errors.instructions}</span>
                  )}
                  <small className="order-hint">
                    Be as detailed as possible. Include rubric, grading criteria, and any important notes.
                  </small>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaFileWord className="order-section-icon" />
                  Supporting Files (Optional)
                </h3>
                
                <div className="order-file-upload">
                  <label className="order-file-label">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      multiple
                      className="order-file-input"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                    <div className="order-file-dropzone">
                      <FaFileWord className="order-file-icon" />
                      <p>Click to upload or drag & drop files</p>
                      <small>PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB each)</small>
                    </div>
                  </label>

                  {formData.files.length > 0 && (
                    <div className="order-file-list">
                      <h4>Uploaded Files:</h4>
                      {formData.files.map((file, index) => (
                        <div key={index} className="order-file-item">
                          <span className="order-file-name">{file.name}</span>
                          <span className="order-file-size">{formatFileSize(file.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="order-file-remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="order-form-column">
              {/* Formatting Requirements */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaBook className="order-section-icon" />
                  Formatting Requirements
                </h3>
                
                <div className="order-requirements-grid">
                  <div className="order-form-group">
                    <label className="order-form-label">
                      Spacing
                    </label>
                    <select
                      name="spacing"
                      value={formData.spacing}
                      onChange={handleChange}
                      className="order-select"
                    >
                      {spacingOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      Format Style
                    </label>
                    <select
                      name="format"
                      value={formData.format}
                      onChange={handleChange}
                      className="order-select"
                    >
                      {formatOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      <FaLanguage className="order-input-icon" />
                      Language
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      className="order-select"
                    >
                      {languageOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      Number of Sources
                    </label>
                    <input
                      type="number"
                      name="sources"
                      value={formData.sources}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      className="order-input"
                    />
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      Slides (if presentation)
                    </label>
                    <input
                      type="number"
                      name="slides"
                      value={formData.slides}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      className="order-input"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Services - WITH DYNAMIC PRICING */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaUniversity className="order-section-icon" />
                  Additional Services
                </h3>
                
                <div className="order-services-grid">
                  {/* Plagiarism Report */}
                  <div className="order-service-item">
                    <label className="order-service-label">
                      <input
                        type="checkbox"
                        name="plagiarismReport"
                        checked={formData.plagiarismReport}
                        onChange={handleChange}
                        className="order-checkbox"
                      />
                      <span className="order-service-text">
                        <strong>{getServiceName('PLAGIARISM') || 'Plagiarism Report'}</strong>
                        <small>{getServiceDescription('PLAGIARISM') || 'Turnitin report included'}</small>
                      </span>
                      <span className="order-service-price">
                        {getServicePrice('PLAGIARISM') || '+$10'}
                      </span>
                    </label>
                  </div>

                  {/* Executive Summary */}
                  <div className="order-service-item">
                    <label className="order-service-label">
                      <input
                        type="checkbox"
                        name="executiveSummary"
                        checked={formData.executiveSummary}
                        onChange={handleChange}
                        className="order-checkbox"
                      />
                      <span className="order-service-text">
                        <strong>{getServiceName('EXECUTIVE_SUMMARY') || 'Executive Summary'}</strong>
                        <small>{getServiceDescription('EXECUTIVE_SUMMARY') || '1-page summary of key points'}</small>
                      </span>
                      <span className="order-service-price">
                        {getServicePrice('EXECUTIVE_SUMMARY') || '+$15'}
                      </span>
                    </label>
                  </div>

                  {/* Expert Writer */}
                  <div className="order-service-item">
                    <label className="order-service-label">
                      <input
                        type="checkbox"
                        name="expertWriter"
                        checked={formData.expertWriter}
                        onChange={handleChange}
                        className="order-checkbox"
                      />
                      <span className="order-service-text">
                        <strong>{getServiceName('EXPERT_WRITER') || 'Expert Writer'}</strong>
                        <small>{getServiceDescription('EXPERT_WRITER') || 'Top-rated specialist in your field'}</small>
                      </span>
                      <span className="order-service-price">
                        {getServicePrice('EXPERT_WRITER') || '+$25'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              <div className="order-price-summary">
                <h3 className="order-price-title">
                  <FaQuoteRight className="order-price-icon" />
                  Price Estimate
                </h3>
                
                <div className="order-price-breakdown">
                  <div className="order-price-row">
                    <span>Base Price ({formData.pages} pages)</span>
                    <span>${priceBreakdown.basePrice}</span>
                  </div>
                  
                  {priceBreakdown.rushFee > 0 && (
                    <div className="order-price-row order-price-rush">
                      <span>Rush Fee ({getRushFeeDescription()})</span>
                      <span>+${priceBreakdown.rushFee}</span>
                    </div>
                  )}
                  
                  {priceBreakdown.serviceFee > 0 && (
                    <div className="order-price-row">
                      <span>Additional Services</span>
                      <span>+${priceBreakdown.serviceFee}</span>
                    </div>
                  )}
                  
                  <div className="order-price-row order-price-total">
                    <strong>Total Estimate</strong>
                    <strong>${priceBreakdown.total}</strong>
                  </div>
                </div>

                {formData.deadline && (
                  <div className="order-deadline-summary">
                    <p><strong>Deadline:</strong> {formatDateForDisplay(formData.deadline)}</p>
                  </div>
                )}

                <div className="order-price-note">
                  <FaInfoCircle className="order-note-icon" />
                  <p>This is an estimate. Final price will be confirmed before payment.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="order-submit-container">
            <button 
              type="submit" 
              className="order-submit-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="order-spinner"></span>
                  Processing Order...
                </>
              ) : (
                <>
                  <FaPaperPlane className="order-button-icon" />
                  {isAuthenticated ? "Preview Order & Continue" : "Submit Order & Login"}
                </>
              )}
            </button>
            
            <p className="order-note">
              {isAuthenticated ? (
                <>
                  Logged in as <strong>{user?.username || user?.email}</strong>. 
                  You'll review your order details before proceeding to payment.
                </>
              ) : (
                "You'll be asked to login or create an account to continue with your order."
              )}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderPage;
